// === FEATURE: Static FM ===
// A skeuomorphic brushed-metal radio tuner with bronze rivets. Derived from
// CassetteDeck (the metal/rivet chrome, dot-matrix + receipt readouts, the
// bar-level meter idiom) and MarqueeCard (the scrolling surreal banner).
//
// A draggable/clickable rotary KNOB rotates a NEEDLE across a horizontal
// 88 -> 108 frequency dial. The dial snaps to a handful of stations; each tuned
// station reveals its own surreal scrolling marquee line plus a small bar-level
// meter that "responds" to the signal. Between stations you get visual static
// (a cheap CSS texture) and the word HISS.
//
// PERF: the bar meter is driven by a SINGLE requestAnimationFrame loop that only
// runs while the component is on-screen AND the tab is visible AND we are tuned
// to a station (whenVisible gate from the shared util + a tuned check). Static is
// a pure-CSS texture with no JS. Pointer drags are coalesced with rafThrottle.
// There is no always-on timer.

import * as React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  whenVisible,
  rafThrottle,
  prefersReducedMotion,
} from "../_shared/sketchFrame";
import audioBus from "../_shared/audioBus";
import FMAudio from "./fmAudio";
import data from "./stations.json";
import "./staticfm.css";

const MIN = data.min;
const MAX = data.max;
const SPAN = MAX - MIN;
const STATIONS = data.stations;

// Frequency the tuner defaults to the moment POWER is flipped ON. 101.8 MHz is
// not itself a station call (the band's stations live in stations.json), so the
// dial/readout simply parks here; the receiver then locks onto whatever station
// lies within LOCK_TOL (WOMB 101.9 sits 0.1 MHz away, so it tunes in) — exactly
// as a real set would. Clamped into the band defensively.
const DEFAULT_FREQ = Math.max(MIN, Math.min(MAX, 101.8));

// Stable id this radio uses to claim/release the shared mutual-exclusion bus.
const BUS_ID = "staticfm";

// How close (in MHz) the needle must be to a station to "lock" onto it.
const LOCK_TOL = 0.45;
// Beyond this distance (MHz) from the nearest station, static is at full volume.
// Inside it, static fades and (within LOCK_TOL) the station drone emerges.
const STATIC_FULL = 1.6;
// Number of vertical bars in the level meter.
const BAR_COUNT = 14;
// Number of bars in the tiny MINIMIZED equalizer shown when the dock is stowed.
// Fewer + smaller than the full meter so the minimized face stays subtle.
const MINI_BAR_COUNT = 5;
// Resting / floor scaleY for a bar (also the settled value when the meter is
// off). The real audio level is mapped into [BAR_REST .. 1] so a quiet signal
// still reads as a lit meter rather than a flat line.
const BAR_REST = 0.16;
// Per-frame LERP factor toward each bar's target level — lively but smooth.
const BAR_LERP = 0.32;

// How long the power-on WARM-UP sequence runs (ms) before the unit settles into
// its normal live state. The CRT/tube bring-up (flicker -> filament glow ->
// static swell -> dial/meter calibration sweep -> readout init) is choreographed
// in CSS over this same window (see .fm-shell.is-booting in staticfm.css). Under
// prefers-reduced-motion we skip the whole sequence and go straight to live.
const BOOT_MS = 1500;

// How long the power-OFF SHUTDOWN sequence runs (ms) before the unit settles
// into its dead/cold OFF state. The mirror of the bootup: the readout/glow
// collapse, the CRT shrinks to a dot, the needle drops, the static dies out,
// then the cabinet goes cold (see .fm-shell.is-shutting in staticfm.css). A
// matching audio power-down cue (drone/static duck, a dying-CRT pitch-down
// whine, a relay clunk) is fired from fmAudio.shutdown(). Under
// prefers-reduced-motion we skip the whole sequence and go cold immediately.
const SHUTDOWN_MS = 1100;

// Map a frequency to a 0..1 position along the dial.
function freqToPct(freq) {
  return (freq - MIN) / SPAN;
}

// Index of the nearest station to a frequency (always defined; ties pick first).
function nearestStationIdx(freq) {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < STATIONS.length; i++) {
    const d = Math.abs(STATIONS[i].freq - freq);
    if (d < bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return best;
}

// Find the station the needle is currently locked to, or null (between = static).
function lockedStation(freq) {
  let best = null;
  let bestDist = LOCK_TOL;
  for (let i = 0; i < STATIONS.length; i++) {
    const d = Math.abs(STATIONS[i].freq - freq);
    if (d <= bestDist) {
      best = STATIONS[i];
      bestDist = d;
    }
  }
  return best;
}

// 0 (needle dead-on a station) .. 1 (far between stations) — drives hiss volume
// and is the inverse of how strongly the station drone comes through.
function staticAmountFor(freq) {
  const idx = nearestStationIdx(freq);
  const d = Math.abs(STATIONS[idx].freq - freq);
  return Math.max(0, Math.min(1, d / STATIC_FULL));
}

function StaticFM({ sound = true, compact = false }) {
  // Current dial frequency. Start parked between stations so you land on visible
  // static first and have to tune in.
  const [freq, setFreq] = useState(88.9);
  // The station we are locked onto (null = static/hiss). Derived but stored so the
  // meter effect can depend on it cheaply.
  const [tuned, setTuned] = useState(null);
  // POWER toggle. Default OFF so nothing autoplays — audio only ever starts after
  // the user flips this (which doubles as the required first user gesture).
  const [power, setPower] = useState(false);
  // WARM-UP gate. True for the brief CRT/tube bring-up window right after power
  // is flipped ON: the cabinet runs its boot choreography (flicker -> filament
  // glow -> static swell -> dial/meter calibration sweep -> readout init) and the
  // live analyser-meter is held off until it clears, so the boot owns the bars.
  const [booting, setBooting] = useState(false);
  // SHUTDOWN gate. True for the brief power-down window right after power is
  // flipped OFF: the cabinet runs its shutdown choreography (readout/glow
  // collapse -> CRT shrink-to-dot -> needle drop -> static die-out -> cold) and
  // the live analyser-meter is held off until it clears, so the shutdown owns
  // the bars. Mutually exclusive with `booting`. Skipped under reduced motion.
  const [shutting, setShutting] = useState(false);

  const reduce = prefersReducedMotion();

  // The synthesized Web Audio engine (lazily created on first power-on gesture).
  const audioRef = useRef(null);

  // Refs: the shell (visibility gate target), the dial track (for click-to-tune
  // geometry) and the live bar DOM nodes (the rAF writes transforms directly to
  // these instead of calling setState every frame).
  const shellRef = useRef(null);
  const dialRef = useRef(null);
  const barRefs = useRef([]);
  // DOM nodes for the tiny MINIMIZED equalizer (the small "playing" tell shown
  // while the dock is stowed). The same meter rAF writes their scaleY directly so
  // the minimized face dances to the live signal without a second loop. They live
  // in the same housing whether or not we're compact, so the loop can always find
  // them — CSS just hides whichever face isn't active.
  const miniBarRefs = useRef([]);

  // rAF + visibility bookkeeping.
  const rafId = useRef(null);
  const onScreen = useRef(false);
  const draggingFrom = useRef(null); // knob drag anchor: { startX, startFreq }
  // setTimeout handle that ends the warm-up window (cleared if power goes off
  // mid-boot or on unmount, so a stale timer never settles a dead unit "on").
  const bootTimer = useRef(null);
  // setTimeout handle that ends the shutdown window (cleared if power comes back
  // on mid-shutdown or on unmount, so a stale timer never lingers on a live unit).
  const shutTimer = useRef(null);

  // Reused scratch buffers for the REAL audio meter (allocated once, never per
  // frame): `levels` receives the engine's normalized band levels each tick;
  // `barVals` holds each bar's smoothed scaleY so we can LERP toward the target.
  const levels = useRef(new Float32Array(BAR_COUNT));
  const barVals = useRef(new Float32Array(BAR_COUNT).fill(BAR_REST));
  // Smoothed scaleY for each MINIMIZED equalizer bar (own LERP state so it eases
  // independently of the full meter). The mini face samples a spread of bands
  // from the same `levels` buffer the full meter fills, so both faces read the
  // identical live signal.
  const miniVals = useRef(new Float32Array(MINI_BAR_COUNT).fill(BAR_REST));

  // Recompute the locked station whenever the frequency changes.
  useEffect(() => {
    const next = lockedStation(freq);
    // Only setState when the locked station identity actually changes.
    setTuned((prev) => {
      const prevFreq = prev ? prev.freq : null;
      const nextFreq = next ? next.freq : null;
      return prevFreq === nextFreq ? prev : next;
    });
  }, [freq]);

  // --- Audio engine ---------------------------------------------------------
  // Construct the engine once, dispose it (stop oscillators/noise, disconnect,
  // close the AudioContext) on unmount so nothing leaks.
  useEffect(() => {
    const engine = new FMAudio();
    audioRef.current = engine;
    return () => {
      engine.dispose();
      audioRef.current = null;
    };
  }, []);

  // Feed tuning into the engine on every dial move: which station (if any) is
  // locked, and how much static the needle should be hissing. The engine ramps
  // all gains smoothly, so sweeping the band fades static out as a station
  // emerges — like a real receiver.
  useEffect(() => {
    const engine = audioRef.current;
    if (!engine) return;
    const idx = tuned ? STATIONS.indexOf(tuned) : null;
    engine.setTuning(idx, staticAmountFor(freq));
  }, [freq, tuned]);

  // Mirror POWER into the engine. Flipping power ON is the user gesture that
  // lazily creates + resumes the AudioContext.
  //
  // POWER is also what makes the radio AUDIBLE, so it's the moment to coordinate
  // the shared mutual-exclusion audioBus:
  //   - On power ON  -> claim the bus. pauseSelf POWERS THE RADIO OFF, so when
  //     another source (deck/bumper) later claims, StaticFM flips power off and
  //     the engine goes quiet (the power effect re-runs and calls setPower(false)).
  //   - On power OFF -> release the bus so we stop owning it.
  useEffect(() => {
    const engine = audioRef.current;
    if (!engine) return;
    if (power) {
      // Power-on parks the tuner at its DEFAULT frequency (101.8 MHz) so the set
      // always comes up on the same spot, like a real radio with a fixed preset.
      setFreq(DEFAULT_FREQ);
      engine.ensure();
      // pauseSelf: power the radio OFF. Setting power false flushes back through
      // THIS effect on the next render -> engine.setPower(false) silences it.
      // levelFn: expose the radio's live loudness (0..1, averaged across the
      // master-tap analyser) so the shared bus — and any visual reading
      // audioBus.getLevel() — reacts to the station/static while we own it.
      audioBus.claim(
        BUS_ID,
        () => setPower(false),
        () => engine.getLevel()
      );
      // Coming on cancels any in-flight SHUTDOWN window so a stale timer can't
      // later flip the (now-live) unit back into its cold OFF visuals.
      if (shutTimer.current != null) {
        clearTimeout(shutTimer.current);
        shutTimer.current = null;
      }
      setShutting(false);
      // Kick off the CRT/tube WARM-UP. Under reduced motion we skip it entirely
      // and let the unit come up live immediately. Otherwise hold `booting` for
      // the BOOT_MS window while the cabinet runs its bring-up choreography, then
      // settle into the normal live state. A brief audio bring-up cue (relay tick
      // + filament hum + static swell) rides alongside the visuals.
      if (bootTimer.current != null) clearTimeout(bootTimer.current);
      if (reduce) {
        setBooting(false);
      } else {
        setBooting(true);
        engine.boot();
        bootTimer.current = setTimeout(() => {
          bootTimer.current = null;
          setBooting(false);
        }, BOOT_MS);
      }
    } else {
      audioBus.release(BUS_ID);
      // Power off mid-boot: cancel the warm-up so a stale timer can't settle a
      // dead unit back "on".
      if (bootTimer.current != null) {
        clearTimeout(bootTimer.current);
        bootTimer.current = null;
      }
      setBooting(false);
      // Kick off the CRT/tube SHUTDOWN — the mirror of the warm-up. Under reduced
      // motion we skip it and drop straight to the cold OFF state. Otherwise hold
      // `shutting` for the SHUTDOWN_MS window while the cabinet runs its power-down
      // choreography (readout/glow collapse -> CRT shrink-to-dot -> needle drop ->
      // static die-out), then settle into the dead OFF state. A matching audio
      // power-down cue (relay clunk + dying-CRT whine + static fade) rides along.
      if (shutTimer.current != null) clearTimeout(shutTimer.current);
      if (reduce) {
        setShutting(false);
      } else {
        setShutting(true);
        engine.shutdown();
        shutTimer.current = setTimeout(() => {
          shutTimer.current = null;
          setShutting(false);
        }, SHUTDOWN_MS);
      }
    }
    engine.setPower(power);
  }, [power, reduce]);

  // On unmount, make sure we don't leave a stale claim owning the bus (which
  // would let our now-gone pauseSelf be invoked, or block others' UI cues).
  useEffect(() => {
    return () => {
      audioBus.release(BUS_ID);
      if (bootTimer.current != null) {
        clearTimeout(bootTimer.current);
        bootTimer.current = null;
      }
      if (shutTimer.current != null) {
        clearTimeout(shutTimer.current);
        shutTimer.current = null;
      }
    };
  }, []);

  // Honor the global FloorToolbar Music toggle via the `sound` prop. That toggle
  // only mutes <audio> elements, not Web Audio, so we enforce it here: when
  // sound is false the engine forces master gain to 0 regardless of power.
  useEffect(() => {
    const engine = audioRef.current;
    if (!engine) return;
    engine.setSound(sound);
  }, [sound]);

  const togglePower = useCallback(() => setPower((p) => !p), []);

  // The rig is actually making sound only when powered AND not globally muted.
  const live = power && sound !== false;
  // The analyser-driven SIGNAL meter only runs once the warm-up has settled — the
  // boot sequence owns the bars (a CSS calibration sweep) until then, after which
  // the real master-tap meter takes over for the live state.
  const meterLive = live && !booting;

  // --- The level meter loop -------------------------------------------------
  // One rAF that pokes each bar's scaleY. It is started only when we are BOTH
  // on-screen/tab-visible AND tuned to a station; otherwise it never runs.
  const startMeter = useCallback(() => {
    if (rafId.current != null) return; // already running
    if (reduce) return; // honor reduced-motion: leave bars static
    const lv = levels.current;
    const bv = barVals.current;
    const mv = miniVals.current;
    const tick = () => {
      const engine = audioRef.current;
      const bars = barRefs.current;
      const miniBars = miniBarRefs.current;
      // Pull the REAL output spectrum (one band per bar, low->high) from the
      // engine's analyser tap. No-ops to zeros if the graph isn't up yet, so the
      // bars simply settle to rest rather than faking motion.
      if (engine) engine.getLevels(lv, BAR_COUNT);
      else lv.fill(0);
      for (let i = 0; i < bars.length; i++) {
        const el = bars[i];
        if (!el) continue;
        // Map the 0..1 audio level into a tasteful [BAR_REST .. 1] range so a
        // quiet signal still reads as a lit meter, then LERP toward it so the
        // bars are lively but smooth, not jittery.
        const target = BAR_REST + (lv[i] || 0) * (1 - BAR_REST);
        const v = bv[i] + (target - bv[i]) * BAR_LERP;
        bv[i] = v;
        el.style.transform = "scaleY(" + v.toFixed(3) + ")";
      }
      // Drive the tiny MINIMIZED equalizer from the SAME spectrum. We spread its
      // few bars across the full band (low..high) so it reads as a real, lively
      // mini-EQ even with only a handful of bars. Same rest-floor + LERP so it
      // stays subtle and smooth, and calms to rest when the signal is quiet.
      for (let i = 0; i < miniBars.length; i++) {
        const el = miniBars[i];
        if (!el) continue;
        const band =
          miniBars.length > 1
            ? Math.round((i / (miniBars.length - 1)) * (BAR_COUNT - 1))
            : 0;
        const target = BAR_REST + (lv[band] || 0) * (1 - BAR_REST);
        const v = mv[i] + (target - mv[i]) * BAR_LERP;
        mv[i] = v;
        el.style.transform = "scaleY(" + v.toFixed(3) + ")";
      }
      rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
  }, [reduce]);

  const stopMeter = useCallback(() => {
    if (rafId.current != null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
    // Settle the bars to a low resting state so a paused meter doesn't look frozen
    // mid-spike, and reset the smoothing buffer so a restarted meter eases up
    // from rest rather than from a stale spike.
    const bars = barRefs.current;
    const bv = barVals.current;
    for (let i = 0; i < bars.length; i++) {
      bv[i] = BAR_REST;
      if (bars[i]) bars[i].style.transform = "scaleY(" + BAR_REST + ")";
    }
    // Settle the MINIMIZED equalizer to rest too so a stowed-but-paused dock
    // doesn't freeze mid-spike.
    const miniBars = miniBarRefs.current;
    const mv = miniVals.current;
    for (let i = 0; i < miniBars.length; i++) {
      mv[i] = BAR_REST;
      if (miniBars[i]) miniBars[i].style.transform = "scaleY(" + BAR_REST + ")";
    }
  }, []);

  // Visibility gate: track on-screen/tab-visible state. We do NOT start the meter
  // here directly — we just record the flag and reconcile, because whether the
  // meter should run also depends on whether the rig is live.
  useEffect(() => {
    const reconcile = () => {
      // Drive the signal meter whenever the radio is actually powered + audible
      // and on-screen — a dead/muted/off-screen set shows no signal. We do NOT
      // gate on `tuned`: the analyser taps the real master output, so between
      // stations the meter rides the static hiss and, once locked, it dances to
      // the station's music.
      if (onScreen.current && meterLive) startMeter();
      else stopMeter();
    };
    const cleanup = whenVisible(
      shellRef.current,
      () => {
        onScreen.current = true;
        reconcile();
      },
      () => {
        onScreen.current = false;
        reconcile();
      }
    );
    // Run once for the initial mount state (whenVisible only fires on transitions;
    // if it degraded to a single onEnter, onScreen is already set by then).
    reconcile();
    return () => {
      cleanup();
      stopMeter();
    };
    // `meterLive` is intentionally a dep: when we toggle power/mute — or when the
    // warm-up window ends — while already on-screen, this re-runs and reconciles
    // the meter without a second observer. We no longer depend on `tuned` — the
    // rAF reads the real master output every frame, so it keeps running across
    // station locks (riding the static between stations, dancing to the music
    // when tuned). It stays parked during boot so the calibration sweep owns the
    // bars, then takes over the instant the unit settles live.
  }, [meterLive, startMeter, stopMeter]);

  // --- Tuning input ---------------------------------------------------------
  // Set frequency from a clientX over the dial track (click-to-tune).
  const tuneFromClientX = useCallback((clientX) => {
    const el = dialRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    let pct = (clientX - rect.left) / rect.width;
    pct = Math.max(0, Math.min(1, pct));
    setFreq(+(MIN + pct * SPAN).toFixed(1));
  }, []);

  const onDialClick = useCallback(
    (e) => {
      tuneFromClientX(e.clientX);
    },
    [tuneFromClientX]
  );

  // Knob drag: horizontal drag turns the knob and sweeps the band. Coalesced to
  // one update per frame via rafThrottle.
  const onKnobMove = useRef(
    rafThrottle((clientX) => {
      const anchor = draggingFrom.current;
      if (!anchor) return;
      const dx = clientX - anchor.startX;
      // ~260px of drag covers the whole 88..108 band.
      const next = anchor.startFreq + (dx / 260) * SPAN;
      setFreq(+Math.max(MIN, Math.min(MAX, next)).toFixed(1));
    })
  );

  useEffect(() => {
    const moveThrottled = onKnobMove.current;
    const handleMove = (e) => {
      if (!draggingFrom.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      moveThrottled(cx);
    };
    const handleUp = () => {
      draggingFrom.current = null;
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: true });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
      moveThrottled.cancel();
    };
  }, []);

  const onKnobDown = useCallback(
    (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      draggingFrom.current = { startX: cx, startFreq: freq };
    },
    [freq]
  );

  // Snap straight to a station when its preset button is clicked.
  const presetTo = useCallback((f) => setFreq(f), []);

  // --- Derived render values ------------------------------------------------
  const needlePct = freqToPct(freq) * 100;
  // Knob rotation: full band -> ~300deg sweep of the indicator.
  const knobAngle = -150 + freqToPct(freq) * 300;
  const isStatic = !tuned;

  const bars = [];
  for (let i = 0; i < BAR_COUNT; i++) bars.push(i);

  return (
    <div
      className={
        "fm-shell" +
        (live ? " is-on" : " is-off") +
        (booting ? " is-booting" : "") +
        (shutting ? " is-shutting" : "") +
        (compact ? " is-compact" : "")
      }
      ref={shellRef}
    >
      {/* ---- MINIMIZED FACE ----
          A much-smaller, subtle representation of the radio shown only while the
          dock is stowed (`compact`). The full cabinet below stays mounted so the
          engine / audioBus claim / analyser meter all keep running (playback does
          NOT stop when minimized); CSS just hides the cabinet and reveals this
          face. It shows the live call-sign + frequency, a tiny analyser-driven
          equalizer and a pulsing signal glow so it's obvious music is playing,
          plus its own small PWR control so the radio stays operable while stowed. */}
      <div className={"fm-mini" + (live ? " is-on" : " is-off")} aria-hidden={!compact}>
        <button
          type="button"
          className={"fm-mini-power" + (power ? " is-on" : "")}
          onClick={togglePower}
          role="switch"
          aria-checked={power}
          aria-label="Power"
          title={power ? "Power off" : "Power on"}
          tabIndex={compact ? 0 : -1}
        >
          <span className="fm-mini-led" aria-hidden="true" />
        </button>

        <div className="fm-mini-readout">
          <span className="fm-mini-call">
            {booting
              ? "INIT"
              : shutting
              ? "PWR·DN"
              : !live
              ? "OFF"
              : tuned
              ? tuned.call
              : "HISS"}
          </span>
          <span className="fm-mini-freq">
            {live && !booting && !shutting ? freq.toFixed(1) + " MHz" : "STATIC·FM"}
          </span>
        </div>

        {/* Tiny analyser-driven equalizer — the subtle "playing" tell. Driven by
            the same meter rAF (writes scaleY to these nodes). A soft signal glow
            pulses behind it while live (CSS), calmed when paused/off. */}
        <div
          className={"fm-mini-eq" + (meterLive ? " is-live" : "")}
          aria-hidden="true"
        >
          {Array.from({ length: MINI_BAR_COUNT }).map((_, i) => (
            <span
              key={i}
              className="fm-mini-bar"
              ref={(el) => {
                miniBarRefs.current[i] = el;
              }}
            />
          ))}
        </div>
      </div>

      {/* ---- FULL CABINET ----
          The full-size radio. Wrapped so the minimized state can hide it with CSS
          while keeping it MOUNTED (engine / bus / meter intact). aria-hidden when
          compact so the minimized face is the only thing exposed to AT. */}
      <div className="fm-full" aria-hidden={compact}>
      {/* Hazy/retro PATINA — a non-interactive CRT-glass overlay that reuses the
          site's own filter recipes (page-filter grain+scanline, vhs-overlay
          chromatic aberration + tracking band, annex vignette) so the radio
          reads as old, weathered glass-and-metal. Sits above the chrome but is
          pointer-events:none, so every control stays clickable + legible. */}
      <div className="fm-patina" aria-hidden="true" />

      {/* Four bronze rivets, one per corner — matches the deck chrome */}
      <div className="fm-rivot fm-rivot-tl" />
      <div className="fm-rivot fm-rivot-tr" />
      <div className="fm-rivot fm-rivot-bl" />
      <div className="fm-rivot fm-rivot-br" />

      {/* Brand plate + brushed-metal POWER toggle (lights up when on) */}
      <div className="fm-plate">
        <span className="fm-plate-name">STATIC&middot;FM</span>
        <div className="fm-plate-right">
          <span className="fm-plate-sub">solid state // band II</span>
          <button
            type="button"
            className={"fm-power" + (power ? " is-on" : "")}
            onClick={togglePower}
            role="switch"
            aria-checked={power}
            aria-label="Power"
            title={power ? "Power off" : "Power on"}
          >
            <span className="fm-power-led" aria-hidden="true" />
            <span className="fm-power-text">PWR</span>
          </button>
        </div>
      </div>

      {/* The glass: marquee or static, plus the call-sign + frequency readout */}
      <div className={"fm-glass" + (isStatic ? " is-static" : "")}>
        {/* Cheap CSS static texture; only painted when between stations */}
        <div className="fm-static" aria-hidden="true" />

        {/* Power-on WARM-UP overlay. Only mounted during the boot window: a
            tube/filament warm glow rises, a static swell rushes the glass, and a
            calibration scan sweeps across before the live state takes over. The
            readout below shows an initializing message during this same window.
            Inert (pointer-events:none, aria-hidden) so controls stay clickable. */}
        {booting && (
          <div className="fm-boot" aria-hidden="true">
            <div className="fm-boot-glow" />
            <div className="fm-boot-scan" />
          </div>
        )}

        {/* Power-off SHUTDOWN overlay. Only mounted during the shutdown window:
            the live glow collapses, the picture pinches to a horizontal line and
            then a single bright dot that fades to black — the classic dying-CRT
            power-down — as the static dies out underneath. The readout below
            shows a powering-down message during this same window. Inert
            (pointer-events:none, aria-hidden) so it never blocks the controls. */}
        {shutting && (
          <div className="fm-shutdown" aria-hidden="true">
            <div className="fm-shutdown-glow" />
            <div className="fm-shutdown-collapse" />
            <div className="fm-shutdown-dot" />
          </div>
        )}

        {isStatic ? (
          <div className="fm-hiss">
            <span className="fm-hiss-text">- - - HISS - - - NO SIGNAL - - -</span>
          </div>
        ) : (
          <div
            className="fm-marquee"
            style={{ color: tuned.color, ["--fm-glow"]: tuned.color }}
          >
            {/* Two copies so the loop is seamless, like MarqueeCard */}
            <span
              className={"fm-marquee-text" + (reduce ? " no-anim" : "")}
            >
              {tuned.line}
            </span>
            <span
              className={"fm-marquee-text" + (reduce ? " no-anim" : "")}
              aria-hidden="true"
            >
              {tuned.line}
            </span>
          </div>
        )}

        {/* Dot-matrix readout: call sign + frequency. During the warm-up it
            shows the receiver INITIALIZING, and during the power-down it shows
            POWERING DOWN, rather than the live station/freq. */}
        <div className="fm-readout">
          <span className="fm-callsign">
            {booting ? "INIT" : shutting ? "PWR·DN" : tuned ? tuned.call : "-----"}
          </span>
          <span className="fm-freq">
            {booting ? (
              <span className="fm-boot-readout">- - SCAN - -</span>
            ) : shutting ? (
              <span className="fm-shutdown-readout">- OFF -</span>
            ) : (
              <>
                {freq.toFixed(1)}
                <span className="fm-freq-unit"> MHz</span>
              </>
            )}
          </span>
        </div>
      </div>

      {/* The horizontal dial: ticks, station pips, sweeping needle */}
      <div className="fm-dial" ref={dialRef} onClick={onDialClick}>
        {/* tick marks every ~2 MHz */}
        <div className="fm-ticks" aria-hidden="true">
          {Array.from({ length: SPAN + 1 }).map((_, i) => (
            <div
              key={i}
              className={"fm-tick" + (i % 2 === 0 ? " fm-tick-major" : "")}
              style={{ left: (i / SPAN) * 100 + "%" }}
            />
          ))}
        </div>

        {/* station call-sign pips */}
        {STATIONS.map((s) => (
          <button
            key={s.call}
            type="button"
            className={
              "fm-pip" +
              (tuned && tuned.freq === s.freq ? " is-locked" : "")
            }
            style={{ left: freqToPct(s.freq) * 100 + "%", color: s.color }}
            onClick={(e) => {
              e.stopPropagation();
              presetTo(s.freq);
            }}
            aria-label={"Tune to " + s.call + " " + s.freq}
          >
            <span className="fm-pip-dot" />
            <span className="fm-pip-label">{s.call}</span>
          </button>
        ))}

        {/* the sweeping needle */}
        <div
          className="fm-needle"
          style={{ left: needlePct + "%" }}
          aria-hidden="true"
        />
      </div>

      {/* Lower console: the rotary tuning knob + the bar-level meter */}
      <div className="fm-console">
        <div className="fm-knob-wrap">
          <div
            className="fm-knob"
            style={{ transform: "rotate(" + knobAngle + "deg)" }}
            onMouseDown={onKnobDown}
            onTouchStart={onKnobDown}
            role="slider"
            aria-label="Tuning knob"
            aria-valuemin={MIN}
            aria-valuemax={MAX}
            aria-valuenow={freq}
            tabIndex={0}
          >
            <div className="fm-knob-grip" />
            <div className="fm-knob-indicator" />
          </div>
          <div className="fm-knob-cap">TUNE</div>
        </div>

        <div className="fm-meter">
          <div className="fm-meter-bars">
            {bars.map((i) => (
              <div
                key={i}
                className="fm-bar"
                ref={(el) => {
                  barRefs.current[i] = el;
                }}
              />
            ))}
          </div>
          <div className="fm-meter-label">SIGNAL</div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default StaticFM;
